/**
 * GENERATED_ASSET — folded paper boat ported from the batch001 static kit (`static-kit/source/boat.ts`).
 * Art is unchanged; only layout was expanded for readability.
 *
 * The hull's underside rests on the origin (0,0) after the lean is applied; `geometry.hull[1]` is the left keel
 * vertex the Hush wardrobe rests on Pip's palm. The clue tab carries the master two-over-one motif.
 */
import type { DraftArtifact, PaperBoatRecipe, Point2 } from './contracts';
import { Construction, anchor, el, n } from './construction';

export function buildPaperBoat(c: Construction): DraftArtifact {
  const r = c.recipe as PaperBoatRecipe;
  const p = r.params;
  const a = (p.leanDeg * Math.PI) / 180;
  const hull = [{ x: -56, y: -22 }, { x: -21, y: 0 }, { x: 22, y: 0 }, { x: 57, y: -25 }];
  const fold = [{ x: -30, y: -21 }, { x: -3, y: -49 }, { x: 30, y: -22 }];
  const rotate = (v: Point2): Point2 => ({ x: v.x * Math.cos(a) - v.y * Math.sin(a), y: v.x * Math.sin(a) + v.y * Math.cos(a) });
  const underside = [rotate(hull[1]), rotate(hull[2])];
  const maxY = Math.max(...underside.map((v) => v.y));
  const touching = underside.filter((v) => Math.abs(v.y - maxY) < 1e-6);
  const contactX = touching.reduce((s, v) => s + v.x, 0) / touching.length;
  const t = (v: Point2): Point2 => {
    const u = rotate(v);
    return { x: u.x - contactX, y: u.y - maxY };
  };
  const polygon = (v: Point2[], fill: string, part: string): string => {
    const pts = v.map(t);
    return c.path(pts.map((u, i) => `${i ? 'L' : 'M'}${n(u.x)} ${n(u.y)}`).join(' ') + 'Z', pts, fill, c.color('outline'), 1.4, { 'data-part': part });
  };
  let body = c.ellipse(0, 2, 36, 3, c.color('outline'), { opacity: c.tokens.style.contactShadowOpacity, 'data-part': 'contact-shadow' }, false);
  body += polygon(fold, c.color('paperLight'), 'far-fold');
  body += polygon(hull, c.color(p.condition === 'clean' ? 'paperLight' : 'paperBase'), 'boat-hull');
  body += polygon([hull[0], { x: 0, y: -10 }, hull[3], hull[2], hull[1]], c.color('paperShadow'), 'near-fold');
  const ridge = t(fold[1]);
  const crease = t({ x: 0, y: -10 });
  const tab = t({ x: -3, y: -31 });
  const hold = t({ x: 0, y: -9 });
  body += c.path(`M${n(ridge.x)} ${n(ridge.y)}L${n(crease.x)} ${n(crease.y)}`, [ridge, crease], 'none', c.color('outlineSoft'), 0.8, {}, false);
  if (r.detail !== 'readable' && p.paperWear > 0) {
    const pts = hull.map(t);
    const d = pts.map((u, i) => `${i ? 'L' : 'M'}${n(u.x)} ${n(u.y)}`).join(' ') + 'Z';
    const clip = c.define('boat-face', (id) => el('clipPath', { id }, el('path', { d })));
    const v = t({ x: -34, y: -13 });
    const u = t({ x: -20, y: -9 });
    body += el('g', { 'clip-path': clip }, c.path(`M${n(v.x)} ${n(v.y)}L${n(u.x)} ${n(u.y)}`, [v, u], 'none', c.color('paperLight'), 1.5,
      { opacity: p.paperWear * 0.65 }, false));
  }
  if (p.clueTab === 'two_over_one') {
    for (const v of c.tokens.masterMotif.centers) {
      body += c.ellipse(tab.x + v.x * 0.25, tab.y + (v.y + 44) * 0.25, 1.65, 1.65, c.color('outlineSoft'), { 'data-part': 'boat-clue' }, false);
    }
  }
  return c.finish(
    body,
    [anchor('base', 0, 0, 'support'), anchor('support', 0, 0, 'support'), anchor('fold_tab', tab.x, tab.y),
      anchor('ridge', ridge.x, ridge.y), anchor('hand_hold', hold.x, hold.y)],
    [],
    { kind: 'bottom-center', point: { x: 0, y: 0 } },
    { hull: hull.map(t), fold: fold.map(t), supportContact: touching.map((v) => ({ x: v.x - contactX, y: v.y - maxY })), leanDeg: p.leanDeg },
    [],
    'ground-dressing',
  );
}
